import { useState, type InputHTMLAttributes, type ReactNode } from 'react';

/*
  Shared shell for the four account screens — sign in, register, forgot and
  reset. One component rather than four copies so they cannot drift: clicking
  "Forgot password?" from a card onto a bare page reads as a broken link.

  The card sits on a warm sand wash so it lifts off the page without needing a
  heavy border.
*/
export function AuthCard({
  title,
  subtitle,
  children,
  footer,
  wide = false,
}: {
  title: string;
  /** Optional line or notice under the title. Omit rather than pad with filler. */
  subtitle?: ReactNode;
  children: ReactNode;
  /** Sits outside the card — secondary links, contact details. */
  footer?: ReactNode;
  /** Room for a two-column form. Sign-in stays narrow; registration needs it. */
  wide?: boolean;
}) {
  return (
    <div className="bg-linear-to-b from-sand-50 to-white px-4 py-12 sm:px-6 lg:py-16">
      <div className={`mx-auto w-full ${wide ? 'max-w-2xl' : 'max-w-md'}`}>
        <div className="rounded-2xl border border-ink-100 bg-white p-6 shadow-xl shadow-ink-950/5 sm:p-8">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-ink-900">
            {title}
          </h1>
          {subtitle && <div className="mt-2 text-[0.9375rem] text-ink-500">{subtitle}</div>}

          <div className="mt-7">{children}</div>
        </div>

        {footer && <div className="mt-6">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Field label with the required marker.
 *
 * The asterisk is aria-hidden and paired with `required` on the input itself —
 * a red star means nothing to a screen reader, which learns the field is
 * mandatory from the attribute instead.
 */
export function Label({
  htmlFor,
  required = false,
  children,
  className = '',
}: {
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={`block text-sm font-medium text-ink-900 ${className}`}>
      {children}
      {required && (
        <span aria-hidden className="ml-0.5 text-red-600">
          *
        </span>
      )}
    </label>
  );
}

/** Same marker for a <fieldset>, which needs a legend rather than a label. */
export function Legend({ required = false, children }: { required?: boolean; children: ReactNode }) {
  return (
    <legend className="block text-sm font-medium text-ink-900">
      {children}
      {required && (
        <span aria-hidden className="ml-0.5 text-red-600">
          *
        </span>
      )}
    </legend>
  );
}

/** The shared input styling, so every account field matches. */
export const authFieldClass =
  'w-full rounded-lg border border-ink-200 bg-white px-4 py-3 text-base text-ink-900 transition-colors focus:border-gold-400 focus:outline-none focus:ring-2 focus:ring-gold-400/30 disabled:bg-ink-50 disabled:text-ink-500';

const lock = (open: boolean) => (
  <svg aria-hidden viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4.75" y="10.25" width="14.5" height="9.5" rx="2.25" />
    <path d={open ? 'M8.25 10.25V7.5a3.75 3.75 0 0 1 7.15-1.55' : 'M8.25 10.25V7.5a3.75 3.75 0 0 1 7.5 0v2.75'} />
    <circle cx="12" cy="14.75" r="1.15" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * A password field with its own show/hide control, so every place a password
 * is typed — sign-in, registration, reset — behaves the same way.
 */
export function PasswordInput({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  const [shown, setShown] = useState(false);
  return (
    <div className={`relative ${className}`}>
      <input {...props} type={shown ? 'text' : 'password'} className={`${authFieldClass} pr-12`} />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        // The icon alone carries no meaning to a screen reader, and the label
        // has to say what the button will do, not what it shows.
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        className="absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-lg text-ink-400 transition-colors hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/40"
      >
        {lock(shown)}
      </button>
    </div>
  );
}
